import { useState, useCallback, useEffect, useRef } from 'react'
import { apiService, ChatEndpoint } from '../services/api'
import { config } from '../services/config'
import { ChatMessage } from '../data/mockData'
import { agentFrontService } from '../services/agentFront'
import { ProjectContext } from '../services/agentFront'
import { mcpAuth } from '../services/mcpAuth'
import { getAllSlashCommands } from '../data/toolCatalog'
import { getMode } from '../data/modeRegistry'
const ENDPOINT_STORAGE_KEY = 'vsm_chat_endpoint'
const MODE_STORAGE_KEY = 'vsm_chat_mode'
function streamText(
  fullText: string,
  onChunk: (partial: string) => void,
  onDone: () => void,
) {
  let i = 0
  // Faster streaming — show full text in ~1s max
  const charsPerTick = Math.max(5, Math.floor(fullText.length / 60))
  const interval = setInterval(() => {
    i += charsPerTick
    if (i >= fullText.length) {
      onChunk(fullText)
      clearInterval(interval)
      onDone()
    } else {
      onChunk(fullText.slice(0, i))
    }
  }, 16)
  return () => clearInterval(interval)
}
export function useChat(initialMessages?: ChatMessage[]) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionId] = useState(() => crypto.randomUUID())

  // Project context — updated by the component that owns useProjectContext
  const projectContextRef = useRef<ProjectContext | undefined>(undefined)
  const setProjectContext = useCallback((ctx: ProjectContext | undefined) => {
    projectContextRef.current = ctx
  }, [])

  // Endpoint mode — persisted in localStorage
  const [endpointMode, setEndpointModeState] = useState<ChatEndpoint>(() => {
    try {
      const stored = localStorage.getItem(ENDPOINT_STORAGE_KEY)
      if (
        stored === 'chat' ||
        stored === 'invoke' ||
        stored === 'query' ||
        stored === 'agentfront'
      )
        return stored as ChatEndpoint
    } catch {}
    return config.chat.defaultEndpoint
  })
  const setEndpointMode = useCallback((mode: ChatEndpoint) => {
    setEndpointModeState(mode)
    try {
      localStorage.setItem(ENDPOINT_STORAGE_KEY, mode)
    } catch {}
  }, [])

  // Mode — persisted in localStorage
  const [chatMode, setChatModeState] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(MODE_STORAGE_KEY)
      if (stored) return stored
    } catch {}
    return 'agent'
  })
  const setChatMode = useCallback((mode: string) => {
    setChatModeState(mode)
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode)
    } catch {}
  }, [])
  const messagesRef = useRef<ChatMessage[]>([])
  const streamCleanupRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 'welcome',
          role: 'agent',
          content:
            '¡Hola! Soy tu **Scrum Master Virtual**. ¿En qué puedo ayudarte hoy?',
          timestamp: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          suggestedFollowups: [
            '¿Cuál es el estado del sprint actual?',
            '¿Hay bloqueadores en el sprint?',
            'Mostrame la velocidad del equipo',
            'Preparame el daily standup',
            'Listame el backlog del proyecto',
            'Mostrame las épicas del proyecto',
          ],
        },
      ])
    }
  }, [])
  useEffect(() => {
    return () => {
      streamCleanupRef.current?.()
    }
  }, [])
  const sendMessage = useCallback(
    async (content: string, projectKey?: string) => {
      // Intercept System Commands
      if (content.startsWith('/')) {
        const cmd = content.split(' ')[0].toLowerCase()
        if (cmd === '/clear') {
          setMessages([])
          return
        }
        if (cmd === '/status') {
          let statusMsg = ''
          try {
            const status = mcpAuth.getStatus()
            statusMsg = `**Estado de Conexión MCP:**\n\n📋 **Confluence:** ${status.confluence ? '✅ Conectado' : '❌ Desconectado'}\n🎯 **Jira:** ${status.jira ? '✅ Conectado' : '❌ Desconectado'}`
          } catch (e) {
            statusMsg = 'No se pudo obtener el estado de conexión.'
          }
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: 'user',
              content,
              timestamp: new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            },
            {
              id: crypto.randomUUID(),
              role: 'agent',
              content: statusMsg,
              timestamp: new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            },
          ])
          return
        }
        if (cmd === '/help') {
          let helpMsg = '**Comandos Disponibles:**\n\n'
          try {
            const cmds = getAllSlashCommands()
            const grouped = cmds.reduce(
              (acc, c) => {
                if (!acc[c.category]) acc[c.category] = []
                acc[c.category].push(c)
                return acc
              },
              {} as Record<string, any[]>,
            )
            Object.entries(grouped).forEach(([cat, list]) => {
              helpMsg += `**${cat}**\n`
              list.forEach((c) => {
                helpMsg += `• \`${c.command}\` - ${c.description}\n`
              })
              helpMsg += '\n'
            })
          } catch (e) {
            helpMsg = 'Error al cargar los comandos.'
          }
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: 'user',
              content,
              timestamp: new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            },
            {
              id: crypto.randomUUID(),
              role: 'agent',
              content: helpMsg,
              timestamp: new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            },
          ])
          return
        }
        if (cmd === '/refresh-tokens') {
          try {
            mcpAuth.clearTokens()
            setMessages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'user',
                content,
                timestamp: new Date().toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              },
              {
                id: crypto.randomUUID(),
                role: 'agent',
                content:
                  'Tokens limpiados. Por favor, volvé a iniciar sesión en Settings.',
                timestamp: new Date().toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              },
            ])
          } catch (e) {}
          return
        }
      }
      setIsLoading(true)
      setError(null)
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
      }
      setMessages((prev) => [...prev, userMsg])

      // Progress indicator message for agentfront
      const progressId = crypto.randomUUID()
      try {
        const history = messagesRef.current
          .filter(
            (m) => !m.content.startsWith('⚠️') && !m.content.startsWith('🔄'),
          )
          .slice(-10)
          .map((m) => ({
            role: m.role === 'agent' ? 'assistant' : 'user',
            content: m.content,
          }))
        let responseData: any
        if (endpointMode === 'agentfront') {
          // Show progress message
          setMessages((prev) => [
            ...prev,
            {
              id: progressId,
              role: 'agent',
              content: '🔄 **Pensando...**',
              timestamp: new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            },
          ])

          // Use Frontend Orchestrator with progress callback and mode
          const result = await agentFrontService.chat(
            content,
            history,
            (progress) => {
              let statusText = ''
              switch (progress.phase) {
                case 'thinking':
                  statusText = '🧠 **Pensando...**'
                  break
                case 'calling_tools':
                  const inProgress = progress.toolsInProgress || []
                  const completed = progress.toolsCompleted || []
                  const roundLabel =
                    progress.round && progress.round > 1
                      ? ` (ronda ${progress.round})`
                      : ''
                  const completedText =
                    completed.length > 0
                      ? `\n✅ ${completed.map((t) => `\`${t}\``).join(', ')}`
                      : ''
                  const pendingText =
                    inProgress.length > 0
                      ? `\n⏳ ${inProgress.map((t) => `\`${t}\``).join(', ')}`
                      : ''
                  statusText = `🔧 **Consultando servicios${roundLabel}...**${completedText}${pendingText}`
                  break
                case 'formatting':
                  const allCompleted = progress.toolsCompleted || []
                  const allCompletedText =
                    allCompleted.length > 0
                      ? `\n✅ ${allCompleted.map((t) => `\`${t}\``).join(', ')}`
                      : ''
                  statusText = `✨ **Preparando respuesta...**${allCompletedText}`
                  break
                default:
                  statusText = '🔄 **Procesando...**'
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === progressId
                    ? {
                        ...m,
                        content: statusText,
                      }
                    : m,
                ),
              )
            },
            chatMode,
            projectContextRef.current,
          )
          responseData = {
            response: result.response,
            mcpCallsSummary: result.toolCalls,
            orchestratorMetadata: {
              totalLatencyMs: result.latencyMs,
            },
            artifacts: result.artifacts,
            tokenUsage: result.tokenUsage,
          }

          // Remove progress message
          setMessages((prev) => prev.filter((m) => m.id !== progressId))
        } else {
          // Use Backend Orchestrator
          const response = await apiService.invokeAgent(
            content,
            sessionId,
            history,
            endpointMode,
            projectKey,
          )
          responseData = response.data
          responseData.orchestratorMetadata = response.orchestratorMetadata
        }
        setIsLoading(false)
        const streamingId = crypto.randomUUID()
        const fullContent = responseData.response
        const agentMsgBase: ChatMessage = {
          id: streamingId,
          role: 'agent',
          content: '',
          timestamp: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          suggestedFollowups: Array.isArray(responseData.suggestedFollowups)
            ? responseData.suggestedFollowups
            : [],
          mcpCalls: (Array.isArray(responseData.mcpCallsSummary)
            ? responseData.mcpCallsSummary
            : []
          ).map((c: any) => c.tool || c.type || c.name || JSON.stringify(c)),
          latencyMs: responseData.orchestratorMetadata?.totalLatencyMs,
          intentConfidence: responseData.intentConfidence,
          warnings: Array.isArray(responseData.warnings)
            ? responseData.warnings
            : [],
          artifacts: Array.isArray(responseData.artifacts)
            ? responseData.artifacts
            : [],
          tokenUsage: responseData.tokenUsage || undefined,
        }
        setMessages((prev) => [...prev, agentMsgBase])
        setIsStreaming(true)
        const cleanup = streamText(
          fullContent,
          (partial) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamingId
                  ? {
                      ...m,
                      content: partial,
                    }
                  : m,
              ),
            )
          },
          () => {
            setIsStreaming(false)
            streamCleanupRef.current = null
          },
        )
        streamCleanupRef.current = cleanup
        setError(null)
      } catch (err: any) {
        setIsLoading(false)
        // Remove progress message if it exists
        setMessages((prev) => prev.filter((m) => m.id !== progressId))
        console.error('Chat error:', err)
        setError(err.message || 'Error de conexión')
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'agent',
            content: `⚠️ **Error:** ${err.message || 'No pude conectar con el servidor.'}`,
            timestamp: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
          },
        ])
      }
    },
    [sessionId, endpointMode, chatMode],
  )

  /** Rate a message with thumbs up/down */
  const rateFeedback = useCallback(
    (messageId: string, rating: 'up' | 'down') => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                feedback: m.feedback === rating ? null : rating,
              }
            : m,
        ),
      )
    },
    [],
  )

  /** Edit a user message — removes everything from that point, returns content to re-send */
  const editMessage = useCallback(
    (messageId: string): string | null => {
      const idx = messages.findIndex((m) => m.id === messageId)
      if (idx === -1 || messages[idx].role !== 'user') return null
      const content = messages[idx].content
      // Remove this message and everything after it
      setMessages((prev) => prev.slice(0, idx))
      return content
    },
    [messages],
  )

  /** Regenerate the last agent response — removes it and re-sends the preceding user message */
  const regenerateResponse = useCallback(
    (messageId: string) => {
      const idx = messages.findIndex((m) => m.id === messageId)
      if (idx === -1 || messages[idx].role !== 'agent') return

      // Find the user message that triggered this response
      let userMsgIdx = idx - 1
      while (userMsgIdx >= 0 && messages[userMsgIdx].role !== 'user') {
        userMsgIdx--
      }
      if (userMsgIdx < 0) return
      const userContent = messages[userMsgIdx].content

      // Remove from the user message onward (will be re-added by sendMessage)
      setMessages((prev) => prev.slice(0, userMsgIdx))

      // Re-send
      setTimeout(() => sendMessage(userContent), 100)
    },
    [messages, sendMessage],
  )
  return {
    messages,
    isLoading,
    isStreaming,
    error,
    sendMessage,
    sessionId,
    endpointMode,
    setEndpointMode,
    chatMode,
    setChatMode,
    rateFeedback,
    editMessage,
    regenerateResponse,
    setProjectContext,
  }
}
